import { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ListChecks, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DynamicFieldGroup } from "@/components/DynamicFieldInput";

function displayValue(def, value) {
  if (value === undefined || value === null || value === "") return "—";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  if (def?.field_type === "checkbox") return value ? "Yes" : "No";
  return String(value);
}

/**
 * Shows (and optionally edits) the Settings-driven conditional fields stored on
 * a ticket. Resolves visible fields from the ticket's context for labels/order.
 */
export default function TicketCustomFields({ ticket, onUpdated }) {
  const { hasPerm } = useAuth();
  const canEdit = hasPerm("tickets.edit");
  const [defs, setDefs] = useState([]);
  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!ticket?.id) return;
    api.get("/dynamic-fields/resolve", {
      params: {
        main_category_id: ticket.main_category_id || undefined,
        sub_category_id: ticket.sub_category_id || undefined,
        issue_type_id: ticket.issue_type_id || undefined,
        department_id: ticket.department_id || undefined,
        channel: ticket.channel || undefined,
        ticket_type: ticket.ticket_type || undefined,
      },
    }).then((r) => setDefs(r.data || [])).catch(() => setDefs([]));
  }, [ticket?.id, ticket?.main_category_id, ticket?.sub_category_id, ticket?.issue_type_id]);

  const cf = ticket.custom_fields || {};
  // stored keys that no longer map to a visible def (config changed) — show raw
  const defKeys = new Set(defs.map((d) => d.key));
  const orphanKeys = Object.keys(cf).filter((k) => !defKeys.has(k));

  if (defs.length === 0 && orphanKeys.length === 0) return null;

  const startEdit = () => { setValues({ ...cf }); setErrors({}); setEditing(true); };
  const save = async () => {
    const e = {};
    defs.forEach((d) => {
      if (!d.required) return;
      const v = values[d.key];
      const empty = v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
      if (empty) e[`cf_${d.key}`] = `${d.label} is required`;
    });
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      await api.patch(`/tickets/${ticket.id}`, { custom_fields: values });
      toast.success("Additional details updated");
      setEditing(false);
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-slate-200" data-testid="ticket-custom-fields">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="font-heading text-base flex items-center gap-2">
          <ListChecks className="w-4 h-4 text-indigo-500" /> Additional Details
        </CardTitle>
        {canEdit && defs.length > 0 && !editing && (
          <Button variant="ghost" size="sm" onClick={startEdit} data-testid="edit-custom-fields-btn">
            <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
          </Button>
        )}
        {editing && (
          <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
            <X className="w-3.5 h-3.5" />
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {editing ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {defs.map((d) => (
                <DynamicFieldGroup
                  key={d.key} def={d} value={values[d.key]}
                  onChange={(v) => setValues((s) => ({ ...s, [d.key]: v }))}
                  error={errors[`cf_${d.key}`]}
                />
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
              <Button size="sm" onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-custom-fields-btn">
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        ) : (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {defs.map((d) => (
              <div key={d.key} data-testid={`cf-view-${d.key}`}>
                <dt className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{d.label}</dt>
                <dd className="text-sm text-slate-700 mt-0.5">{displayValue(d, cf[d.key])}</dd>
              </div>
            ))}
            {orphanKeys.map((k) => (
              <div key={k} data-testid={`cf-view-${k}`}>
                <dt className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{k}</dt>
                <dd className="text-sm text-slate-700 mt-0.5">{displayValue(null, cf[k])}</dd>
              </div>
            ))}
          </dl>
        )}
      </CardContent>
    </Card>
  );
}
